import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Source, Layer } from "react-map-gl/mapbox";

import {
  computePolygonMetrics,
  formatArea,
  formatPerimeter,
} from "../../polygonDraw/helpers";
import MetricLabelMarker from "../../polygonDraw/MetricLabelMarker";

const HISTORY_AREA_FILL_ID = "history-area-fill";
const HISTORY_AREA_LINE_ID = "history-area-line";
const HISTORY_AREA_SOURCE_ID = "history-area-source";

// Colors mirror the draw theme (polygonDraw/drawStyles.js).
const FILL_COLOR = "#4eb3f2";
const LINE_COLOR = "#1f8fe0";

/**
 * Read-only rendering of a history area assessment's polygons on the map,
 * including an area/perimeter chip at each polygon's centroid.
 * `geometry` is a GeoJSON FeatureCollection.
 */
export default function HistoryAreaPolygons({ geometry }) {
  const { t } = useTranslation();

  const metrics = useMemo(() => {
    if (!geometry?.features?.length) return [];
    // Prefer the values stored per polygon; fall back to computing from geometry.
    const derived = computePolygonMetrics(geometry);
    return geometry.features.map((feature, index) => {
      const fallback = derived[index] || {};
      const props = feature.properties || {};
      const area = Number(props.area_sqm);
      const perimeter = Number(props.perimeter_m);
      return {
        id: feature.id ?? index,
        centroid: fallback.centroid,
        area: Number.isFinite(area) ? area : fallback.area || 0,
        perimeter: Number.isFinite(perimeter)
          ? perimeter
          : fallback.perimeter || 0,
      };
    });
  }, [geometry]);

  if (!geometry) return null;

  return (
    <>
      <Source id={HISTORY_AREA_SOURCE_ID} type="geojson" data={geometry}>
        <Layer
          id={HISTORY_AREA_FILL_ID}
          type="fill"
          paint={{ "fill-color": FILL_COLOR, "fill-opacity": 0.16 }}
        />
        <Layer
          id={HISTORY_AREA_LINE_ID}
          type="line"
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={{ "line-color": LINE_COLOR, "line-width": 2.5 }}
        />
      </Source>

      {metrics.map((metric, index) =>
        metric.centroid ? (
          <MetricLabelMarker
            key={metric.id}
            longitude={metric.centroid[0]}
            latitude={metric.centroid[1]}
            primary={`${t("risksMap.polygon.polygonLabel")} ${index + 1}`}
            secondary={`${formatArea(metric.area, t)} · ${formatPerimeter(metric.perimeter, t)}`}
          />
        ) : null,
      )}
    </>
  );
}
